import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const field = "w-full rounded-xl border border-white/10 bg-ink-900 px-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-white/25 focus:outline-none";
const titleFrom = (prompt) => prompt.split(/[,.]/)[0].split(" ").slice(0, 6).join(" ");

export function PublishDialog({ generation, categories, onOpenChange }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  useEffect(() => {
    if (!generation) return;
    setTitle(titleFrom(generation.prompt).slice(0, 80));
    setDescription("");
    setCategory(generation.operation === "edit" ? "Photo edits" : categories[0] || "");
  }, [generation, categories]);
  const publish = useMutation({
    mutationFn: () => api.post("/studio/presets", { generation_id: generation.id, title: title.trim(), description: description.trim(), category }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["studio-presets"] });
      toast.success("Sent for review. It goes public once it's approved.");
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={Boolean(generation)} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-ink-850 sm:max-w-md" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Share as a preset</DialogTitle>
          <DialogDescription className="text-zinc-500">Your image and its prompt become visible to everyone after a quick review. You can remove it any time.</DialogDescription>
        </DialogHeader>
        {generation && <img src={generation.thumb_url} alt="" className="aspect-video w-full rounded-xl object-cover" />}
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Preset name" aria-label="Preset name" className={cn(field, "h-11")} data-testid="publish-title-input" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={240} rows={2} placeholder="What does it do? (optional)" aria-label="Description"
          className={cn(field, "resize-none py-2.5")} data-testid="publish-description-input" />
        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Category" data-testid="publish-category">
          {categories.map((c) => (
            <button key={c} type="button" role="radio" aria-checked={category === c} onClick={() => setCategory(c)} data-testid={`publish-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={cn("h-8 rounded-full px-3 text-xs transition-colors", category === c ? "bg-white text-ink-950" : "border border-white/10 text-zinc-400 hover:text-zinc-100")}>
              {c}
            </button>
          ))}
        </div>
        <button type="button" disabled={!title.trim() || !category || publish.isPending} onClick={() => publish.mutate()} data-testid="publish-submit-button"
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-white text-sm font-semibold text-ink-950 transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-zinc-500">
          {publish.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Send for review
        </button>
      </DialogContent>
    </Dialog>
  );
}
